import type { ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { withTimeSystem } from "../../constants/orbitMetadata";
import type { FormatterMap } from "../../helpers/utils";
import {
  ConjunctionSummaryCard,
  type ConjunctionSummaryCardField,
} from "../conjunctions/ConjunctionSummaryCard";
import {
  getConjunctionMetricTone,
  getProbabilityFamilyTone,
  type ConjunctionRiskConfig,
} from "../conjunctions/conjunctionUiUtils";
import type { EventEvolutionEvent } from "./EventEvolutionTypes";
import {
  MISS_DISTANCE_METRIC,
  PROBABILITY_METRICS,
  SEPARATION_METRICS,
  formatDate,
  formatMetricValueWithConjunctionFormatters,
  getFieldText,
  getPrimaryProbability,
  getRecordId,
  type EventMetricDefinition,
} from "./eventEvolutionUtils";
import { EventMiniObject } from "./EventMiniObject";

interface EventRecordMiniCardProps {
  activeRecordId: string;
  formatters?: FormatterMap;
  index: number;
  record: EventEvolutionEvent;
  riskConfig?: ConjunctionRiskConfig;
}

const recordHoverClassName =
  "hover:-translate-y-0.5 hover:border-sky-300 hover:shadow-lg hover:ring-1 hover:ring-sky-200/70";

const getCardClassName = (isActive: boolean, isClickable: boolean) => {
  const interactionClassName = isClickable
    ? `${recordHoverClassName} cursor-pointer`
    : "";

  if (isActive) {
    return `border-red-200 shadow-red-100/70 bg-red-50/70 ${interactionClassName}`;
  }

  return `border-slate-200 shadow-slate-100/70 bg-white ${interactionClassName}`;
};

export const EventRecordMiniCard = ({
  activeRecordId,
  formatters,
  index,
  record,
  riskConfig,
}: EventRecordMiniCardProps) => {
  const navigate = useNavigate();
  const recordId = getRecordId(record, index);
  const isActive = recordId === activeRecordId;
  const primaryProbability = getPrimaryProbability(record);
  const conjunctionId = record.id && record.id !== "-" ? record.id : undefined;
  const timeToTcaPillClass = "bg-red-50 text-red-700";
  const formatSummaryMetricValue = (
    metric: EventMetricDefinition,
    value = record[metric.id],
  ) =>
    formatMetricValueWithConjunctionFormatters(
      record,
      metric,
      formatters,
      value,
    );
  const renderMiniMetric = (
    metric: EventMetricDefinition,
    label = metric.miniLabel,
    value = record[metric.id],
  ): ConjunctionSummaryCardField => ({
    label,
    value: formatSummaryMetricValue(metric, value),
  });

  const summaryFields: ConjunctionSummaryCardField[] = [
    {
      ...renderMiniMetric(MISS_DISTANCE_METRIC, "MISS DIST."),
      tone: getConjunctionMetricTone(record.minDistance, riskConfig, "minDist"),
    },
    {
      ...renderMiniMetric(SEPARATION_METRICS[0], "RADIAL"),
      tone: getConjunctionMetricTone(record.radial, riskConfig, "radDist"),
    },
    {
      ...renderMiniMetric(SEPARATION_METRICS[1], "ALONG"),
      tone: getConjunctionMetricTone(record.along, riskConfig, "alongDist"),
    },
    {
      ...renderMiniMetric(SEPARATION_METRICS[2], "CROSS"),
      tone: getConjunctionMetricTone(record.cross, riskConfig, "crossDist"),
    },
    {
      ...renderMiniMetric(
        primaryProbability.metric,
        "POC",
        primaryProbability.value,
      ),
      tone: getProbabilityFamilyTone(record.poc, riskConfig),
    },
    {
      ...renderMiniMetric(PROBABILITY_METRICS[2], "SPOC"),
      tone: getProbabilityFamilyTone(record.poc, riskConfig),
    },
    {
      ...renderMiniMetric(PROBABILITY_METRICS[1], "MPOC"),
      tone: getProbabilityFamilyTone(record.poc, riskConfig),
    },
  ];

  const objectCardClassName =
    "inline-flex w-fit max-w-[280px] flex-col rounded-lg border border-slate-200 bg-slate-50/80 px-2 py-1.5";
  const renderObjectCard = (label: string, content: ReactNode) => (
    <div
      className={objectCardClassName}
      onClick={(event) => event.stopPropagation()}
    >
      <div className="mb-1 text-[11px] font-semibold uppercase text-slate-500">
        {label}
      </div>
      {content}
    </div>
  );

  return (
    <ConjunctionSummaryCard
      className={getCardClassName(isActive, Boolean(conjunctionId))}
      headerPill={
        <div className="flex min-w-0 w-full items-center justify-between gap-3">
          <div className="flex min-w-0 gap-3">
            <span
              className={`truncate text-sm font-bold ${
                isActive ? "text-red-800" : "text-slate-950"
              }`}
            >
              {getFieldText(record.bestCDM ?? record.conjunctionIdentifier) ??
                recordId}
            </span>
            {isActive && (
              <span className="rounded-full bg-red-600 px-2 py-0.5 text-xs font-semibold uppercase text-white">
                Displayed
              </span>
            )}
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <span className="rounded-full bg-white px-2.5 py-1 text-xs font-semibold text-slate-600 ring-1 ring-slate-200">
              {`${withTimeSystem("Execution Epoch")}: ${formatDate(
                record.executionTime,
              )}`}
            </span>
            <span
              className={`rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ring-slate-200 ${timeToTcaPillClass}`}
            >
              {withTimeSystem("TCA")}: {formatDate(record.tca)}
            </span>
          </div>
        </div>
      }
      onClick={
        conjunctionId
          ? () => navigate(`/conjunctions/${conjunctionId}/info`)
          : undefined
      }
      onKeyDown={(event) => {
        if (conjunctionId && (event.key === "Enter" || event.key === " ")) {
          event.preventDefault();
          navigate(`/conjunctions/${conjunctionId}/info`);
        }
      }}
      primaryObject={renderObjectCard(
        "Primary",
        <EventMiniObject
          cospar={getFieldText(
            record.target?.spaceObject?.cosparId ?? record.targetCospar,
          )}
          objectId={getFieldText(
            record.target?.spaceObject?.id ?? record.targetId,
          )}
          name={getFieldText(record.target?.spaceObject?.name ?? record.target)}
          norad={getFieldText(
            record.target?.spaceObject?.noradId ?? record.targetNorad,
          )}
          sourceOfInformation={getFieldText(
            record.targetSourceOfInformation ??
              record.target?.spaceObject?.sourceOfInformation,
          )}
          updateTime={record.targetOrbitUpdateTime ?? record.executionTime}
        />,
      )}
      secondaryObject={renderObjectCard(
        "Secondary",
        <EventMiniObject
          cospar={getFieldText(
            record.chaser?.spaceObject?.cosparId ?? record.chaserCospar,
          )}
          objectId={getFieldText(
            record.chaser?.spaceObject?.id ?? record.chaserId,
          )}
          name={getFieldText(record.chaser?.spaceObject?.name ?? record.chaser)}
          norad={getFieldText(
            record.chaser?.spaceObject?.noradId ?? record.chaserNorad,
          )}
          sourceOfInformation={getFieldText(
            record.chaserSourceOfInformation ??
              record.chaser?.spaceObject?.sourceOfInformation,
          )}
          updateTime={record.chaserOrbitUpdateTime ?? record.executionTime}
        />,
      )}
      summaryFields={summaryFields}
    />
  );
};
